import type {DiffEntry, DiffLine} from '@github-ui/diff-lines'
import type {StateCreator} from 'zustand'
import {loadDiffEntriesForPaths} from '../../page-data/loaders/use-diff-entries'
import {
  diffContextLinesKey,
  fetchInjectedContextLines,
  type ContextLineRange,
} from '../../page-data/loaders/use-context-lines-data'
import {getQueryClient} from '@github-ui/react-core/query-client'
import type {ComparisonInfoSlice, ComparisonInfoSliceOpts} from './comparison-info'
import {produce} from 'immer'

export type DiffEntriesSliceOpts = {
  basePath: string
  diffEntries: Record<string, DiffEntry>
} & Pick<ComparisonInfoSliceOpts, 'baseOid' | 'headOid' | 'selectedCommitRange'>

export type DiffEntriesSlice = {
  diffEntries: Record<string, DiffEntry>

  diffEntriesActions: {
    updateDiffEntries: (diffEntries: DiffEntry[]) => void
    loadDiffEntries: (variables: {
      paths: string[]
      contextRanges?: {[path: string]: Array<[number, number]>}
      signal?: AbortSignal
    }) => Promise<DiffEntry[]>
    fetchContextLines: (variables: {path: string; contextLineRanges: ContextLineRange[]; w?: string}) => Promise<void>
  }
}

export const createDiffEntriesSlice =
  ({
    basePath,
    diffEntries,
    baseOid,
    headOid,
    selectedCommitRange,
  }: DiffEntriesSliceOpts): StateCreator<
    DiffEntriesSlice & ComparisonInfoSlice,
    [['zustand/devtools', never]],
    [],
    DiffEntriesSlice
  > =>
  (set, get) => {
    const queryClient = getQueryClient()
    for (const [_, diffEntry] of Object.entries(diffEntries)) {
      const contextLinesKey = diffContextLinesKey(
        basePath,
        diffEntry.path,
        selectedCommitRange.baseOid ?? baseOid,
        selectedCommitRange.headOid ?? headOid,
      )

      // Store the diffEntry's diffLines in Tan Stack Queries' context lines cache
      queryClient.setQueryData(contextLinesKey, () => diffEntry.diffLines)
    }

    return {
      diffEntries,
      diffEntriesActions: {
        updateDiffEntries: diffEntriesToUpdate => {
          const updatedDiffEntries = get().diffEntries ?? {}
          const tempUpdatedDiffEntries: Record<string, DiffEntry> = {}
          for (const diffEntry of diffEntriesToUpdate) {
            // Store difflines in TanStackQuery context lines cache
            const contextLinesKey = diffContextLinesKey(
              basePath,
              diffEntry.path,
              get().comparisonInfo.selectedRange.baseOid,
              get().comparisonInfo.selectedRange.headOid,
            )
            queryClient.setQueryData(contextLinesKey, diffEntry.diffLines)
            // Store diffEntry in updatedDiffEntries
            tempUpdatedDiffEntries[diffEntry.path] = diffEntry
          }

          set(
            {
              diffEntries: produce(updatedDiffEntries, (m: Record<string, DiffEntry>) => {
                for (const diffEntryKey of Object.keys(tempUpdatedDiffEntries)) {
                  if (tempUpdatedDiffEntries[diffEntryKey]) {
                    m[diffEntryKey] = tempUpdatedDiffEntries[diffEntryKey]
                  }
                }
              }),
            },
            undefined,
            'updateDiffEntriesInMap',
          )
        },
        loadDiffEntries: async (variables: {
          paths: string[]
          contextRanges?: {[path: string]: Array<[number, number]>}
          signal?: AbortSignal
        }) => {
          const loadedEntries = await loadDiffEntriesForPaths({
            basePath,
            ...variables,
            ...get().comparisonInfo,
          })
          if (!loadedEntries || loadedEntries.length === 0) {
            return []
          }
          get().diffEntriesActions.updateDiffEntries(loadedEntries)

          return loadedEntries
        },
        fetchContextLines: async ({
          path,
          contextLineRanges,
          w = '0',
        }: {
          path: string
          contextLineRanges: ContextLineRange[]
          w?: string
        }) => {
          const contextLinesKey = diffContextLinesKey(
            basePath,
            path,
            get().comparisonInfo.selectedRange.baseOid,
            get().comparisonInfo.selectedRange.headOid,
          )
          const entryToUpdate = get().diffEntries[path]

          if (!entryToUpdate) return

          await queryClient.fetchQuery({
            queryKey: contextLinesKey,
            queryFn: async () => {
              const json = await fetchInjectedContextLines({
                contextLineRanges,
                basePath,
                path,
                w,
                ...get().comparisonInfo,
              })

              let updatedDiffLines = entryToUpdate.diffLines

              const data = queryClient.setQueryData(contextLinesKey, (old: DiffLine[]) => {
                // Fallback to the entries difflines in case Zustand cache has expired
                updatedDiffLines = mergeContextLines(old ?? entryToUpdate.diffLines, json?.diffEntryLines ?? [])
                return updatedDiffLines
              })

              set(
                (prevState: DiffEntriesSlice) => {
                  return {
                    ...prevState,
                    diffEntries: {
                      ...prevState.diffEntries,
                      [path]: {
                        ...entryToUpdate,
                        diffLines: updatedDiffLines,
                      },
                    },
                  }
                },
                undefined,
                'addContextLinesToDiffEntry',
              )

              return data
            },
          })
        },
      },
    }
  }

export function mergeContextLines(initialDiffLines: DiffLine[], expandedDiffLines: DiffLine[]) {
  const lineMap = new Map<string, (typeof initialDiffLines)[0]>()

  for (const line of initialDiffLines) {
    const key = `${line.left}-${line.right}`
    lineMap.set(key, line)
  }

  // using a map for lookups is faster than using a `find` on the array while iterating through the map
  const mergedDiffLines = expandedDiffLines.map(line => {
    const key = `${line.left}-${line.right}`
    const existingLine = lineMap.get(key)

    if (existingLine) {
      return {...line, position: existingLine.position, displayNoNewLineWarning: existingLine.displayNoNewLineWarning}
    } else {
      // diffLines don't actually care about position and we can't comment on context lines anyway
      return {...line, position: null, threadsData: undefined}
    }
  })

  return mergedDiffLines
}
